import React, { useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import ConfirmationModal from '../components/ConfirmationModal';
import { AlertCircle, CheckCircle, Loader2, Search, Wrench, Database } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

export default function DatabaseMaintenance() {
  const [loading, setLoading] = useState(false);
  const [checkResults, setCheckResults] = useState(null);
  const [fixResults, setFixResults] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: () => {},
    confirmText: 'OK',
    showCancel: false
  });

  const runCheck = async () => {
    setLoading(true);
    setCheckResults(null);
    setFixResults(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/data-maintenance/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCheckResults(response.data);
    } catch (err) {
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: err.response?.data?.detail || 'An error occurred during check',
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    } finally {
      setLoading(false);
    }
  };

  const runFix = async () => {
    setLoading(true);
    setFixResults(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/data-maintenance/fix`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFixResults(response.data);
      // Check again after fix
      await runCheck();
      
      setConfirmModal({
        isOpen: true,
        title: 'Success',
        message: `Successfully fixed ${response.data.totalFixed || 0} inconsistencies!`,
        type: 'success',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    } catch (err) {
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: err.response?.data?.detail || 'An error occurred during fix',
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    } finally {
      setLoading(false);
    }
  };

  const getTitle = (type) => {
    switch(type) {
      case 'old_projects_without_company':
        return 'Old Projects - Company Assignment';
      case 'deleted_projects_with_shared_items':
        return 'Deleted Projects with Shared CSI Items';
      case 'editor_role_users':
        return "Deprecated 'editor' Role";
      default:
        return type;
    }
  };

  const getDescription = (type) => {
    switch(type) {
      case 'old_projects_without_company':
        return 'Assigns projects with companyId=None to their creator\'s company';
      case 'deleted_projects_with_shared_items':
        return 'Cleans up all shares in deleted projects';
      case 'editor_role_users':
        return "Updates editor role to user";
      default:
        return '';
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h2 className="text-3xl font-bold mb-2" style={{ background: '#FF6600', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Database Maintenance
            </h2>
            <p className="text-gray-600">Detect and fix data inconsistencies that may occur after system updates</p>
          </div>

          {/* Info Card */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-blue-900 mb-2">How It Works</h3>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                  <li><strong>Check:</strong> Detects all inconsistencies in the system (makes no changes)</li>
                  <li><strong>Fix:</strong> Automatically corrects all found inconsistencies</li>
                  <li><strong>Safe:</strong> Only fixes inconsistent data, doesn't touch correct data</li>
                </ol>
              </div>
            </div>
            <div className="bg-white border border-blue-200 rounded-lg p-4">
              <p className="font-medium text-sm text-gray-900 mb-2">When Should You Run It?</p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• After every system update</li>
                <li>• Monthly routine check</li>
                <li>• When inconsistency is noticed (wrong numbers, missing information, etc.)</li>
              </ul>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex gap-3">
              <button
                onClick={runCheck}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Search className="w-5 h-5" />
                )}
                Check Inconsistencies
              </button>

              <button
                onClick={runFix}
                disabled={loading || !checkResults || checkResults.totalIssues === 0}
                className="flex items-center gap-2 text-white px-6 py-3 rounded-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: '#FF6600' }}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Wrench className="w-5 h-5" />
                )}
                Fix All Inconsistencies
              </button>
            </div>
          </div>

          {/* Check Results */}
          {checkResults && (
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Check Results</h3>
                <div className="flex items-center gap-2">
                  {checkResults.totalIssues === 0 ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-green-700 font-medium">No inconsistencies found - System is clean!</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-5 h-5 text-orange-600" />
                      <span className="text-orange-700 font-medium">
                        Found {checkResults.totalIssues} inconsistencies that need attention
                      </span>
                    </>
                  )}
                </div>
              </div>

              {checkResults.issues && checkResults.issues.length > 0 && (
                <div className="space-y-3">
                  {checkResults.issues.map((issue) => (
                    <div key={issue.type} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-sm mb-1">{getTitle(issue.type)}</h4>
                          <p className="text-xs text-gray-600 mb-2">{getDescription(issue.type)}</p>
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                              issue.count > 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {issue.count > 0 ? (
                                <>
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  {issue.count} inconsistencies
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Clean
                                </>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {issue.count > 0 && issue.items && issue.items.length > 0 && (
                        <div className="mt-3 border-t pt-3">
                          <p className="text-xs text-gray-500 mb-2">First {Math.min(3, issue.items.length)} examples:</p>
                          <ul className="text-xs space-y-1">
                            {issue.items.slice(0, 3).map((item, idx) => (
                              <li key={idx} className="text-gray-700">
                                • {item.project_name || item.name || item.email || 'Item'}
                                {item.should_have_company && ` → ${item.should_have_company.substring(0, 8)}...`}
                              </li>
                            ))}
                          </ul>
                          {issue.items.length > 3 && (
                            <p className="text-xs text-gray-500 mt-1">... and {issue.items.length - 3} more</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Fix Results */}
          {fixResults && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-green-900 mb-2">Fix Completed Successfully!</h3>
                  <p className="text-sm text-green-800 mb-3">
                    Fixed {fixResults.totalFixed || 0} inconsistencies across all categories.
                  </p>
                  {fixResults.fixed && fixResults.fixed.length > 0 && (
                    <div className="space-y-2">
                      {fixResults.fixed.map((fix, idx) => (
                        <div key={idx} className="text-sm text-green-800">
                          • {getTitle(fix.type)}: <strong>{fix.count}</strong> fixed
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        showCancel={confirmModal.showCancel}
      />
    </AdminLayout>
  );
}
